import { useState, useMemo, useEffect, useCallback } from 'react'
import { View, Dimensions, ScrollView, Platform } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useLocalSearchParams } from 'expo-router'
import {
  getFriendsPageStyles,
  FRIENDS_CONTENT_PADDING_HORIZONTAL,
} from '../../../src/friends/styles/friends.styles'
import { useTheme } from '../../../src/theme/useTheme'
import {
  FriendsPageProvider,
  useFriendsPage,
} from '../../../src/friends/contexts/FriendsPageContext'
import { useFriendsData } from '../../../contexts/FriendsDataContext'
import {
  DecorativeElements,
  InviteFriendsModal,
  FriendsList,
  FriendsGroupsToggle,
  InviteFriendsButton,
  CreateGroupModal,
  PeopleYouMayKnow,
} from '../../../components/friends'
import { FriendsGridSkeleton } from '../../../components/friends/FriendsList/FriendsGridSkeleton'
import { FriendsFilterButtons } from '../../../components/friends/FriendsFilterButtons'
import { PageHeader, ErrorBoundary } from '../../../components/layout'
import { HEADER_FLAVORS } from '../../../components/layout/constants/pageHeader.constants'
import { GuestBanner } from '../../../src/shared/components/GuestBanner/GuestBanner'
import { useFriendData, TEST_DUPLICATE_COUNT } from '../../../src/friends/hooks/useFriendData'
import { useGroupsData } from '../../../src/friends/hooks/useGroupsData'
import { getUnifiedListCounts } from '../../../src/friends/helpers/unifiedList.helper'
import { useSkeletonLoading } from '../../../src/shared/hooks/useSkeletonLoading'
import { FadeInContent } from '../../../components/shared/FadeInContent'
import { DataLoadErrorPanel } from '../../../src/shared/components/DataLoadErrorPanel/DataLoadErrorPanel'
import { FRIENDS_ERROR_COPY } from '../../../src/shared/constants/dataLoadError.constants'
import {
  VIEW_TYPE,
  FILTER_TYPE,
  type ViewType,
  type FilterType,
} from '../../../src/friends/constants/viewFilter.constants'
import { calculateColumns } from '../../../src/shared/helpers/calculateColumns.helper'
import { useReceivedInvitesCount } from '../../../src/friends/hooks/useReceivedInvitesCount'
import { useGuestGuard } from '../../../src/shared/hooks/useGuestGuard'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { useFriendSuggestionsQuery } from '../../../src/friends/hooks/useFriendSuggestionsQuery'
import { useAnalytics } from '../../../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../../src/analytics'

const FRIENDS_TAB_PARAM = 'tab'
const TAB_GROUPS = 'groups'

function FriendsContent() {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const friendsPageStyles = useMemo(() => getFriendsPageStyles(theme), [theme])
  // Web: absolute header; inset slightly under full headerHeight so controls sit just under the bar.
  const belowHeaderPaddingTop = useMemo(
    () => (Platform.OS === 'web' ? insets.top + 4 + 60 - 10 : 0),
    [insets.top]
  )
  const { [FRIENDS_TAB_PARAM]: tabParam } = useLocalSearchParams<{ [FRIENDS_TAB_PARAM]?: string }>()
  const { isGuest, requireAuth } = useGuestGuard()
  const { userId: authUserId, isSignedIn } = useSupabaseAuth()
  const suggestionsUserId = authUserId ?? ''
  const suggestionsEnabled = isSignedIn === true && !isGuest && !!suggestionsUserId
  const { data: suggestionsData } = useFriendSuggestionsQuery(suggestionsUserId, suggestionsEnabled)

  const {
    loading,
    friends,
    groups,
    loadingGroups,
    setShowInviteModal,
    setInviteTab,
    refreshGroups,
    setSnackbar,
  } = useFriendsPage()
  const { loading: friendsDataLoading, error: friendsDataError, refreshFriends } = useFriendsData()

  const screenWidth = Dimensions.get('window').width
  const contentWidth = screenWidth - 2 * FRIENDS_CONTENT_PADDING_HORIZONTAL
  const [viewType, setViewType] = useState<ViewType>(() =>
    tabParam === TAB_GROUPS ? VIEW_TYPE.GROUPS : VIEW_TYPE.FRIENDS
  )

  useEffect(() => {
    if (isGuest) {
      setViewType(VIEW_TYPE.FRIENDS)
      return
    }
    if (tabParam === TAB_GROUPS) setViewType(VIEW_TYPE.GROUPS)
  }, [tabParam, isGuest])
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false)
  const [filterType, setFilterType] = useState<FilterType>('FRIEND')

  useEffect(() => {
    setFilterType(FILTER_TYPE.FRIEND)
  }, [viewType])

  const { items: friendItems } = useFriendData({
    friends,
    duplicateCount: TEST_DUPLICATE_COUNT,
  })

  const { items: groupItems } = useGroupsData({
    groups,
    duplicateCount: TEST_DUPLICATE_COUNT,
  })

  const items = viewType === VIEW_TYPE.FRIENDS ? friendItems : groupItems
  const isLoading = viewType === VIEW_TYPE.FRIENDS ? (loading || friendsDataLoading) : loadingGroups

  const counts = useMemo(
    () => getUnifiedListCounts(items, viewType),
    [items, viewType]
  )

  const { friendReceived: friendReceivedCount, groupReceived: groupReceivedCount } =
    useReceivedInvitesCount()

  const handleFriendsGroupsViewChange = useCallback(
    (next: ViewType) => {
      if (isGuest && next === VIEW_TYPE.GROUPS) return
      setViewType(next)
    },
    [isGuest]
  )

  const { track } = useAnalytics()

  const handleInviteFlow = useCallback(() => {
    if (isGuest) {
      requireAuth('invite friends', '/app/friends')
      return
    }
    track(ANALYTICS_EVENTS.INVITE_MODAL_OPENED, { source: 'friends' })
    setShowInviteModal(true)
    setInviteTab('manual')
  }, [isGuest, requireAuth, setShowInviteModal, setInviteTab, track])

  const { isLoadingWithDelay, contentOpacity } = useSkeletonLoading({
    isLoading,
    isContentReady: items.length > 0 || !isLoading,
  })

  const showFriendsLoadError =
    viewType === VIEW_TYPE.FRIENDS &&
    Boolean(friendsDataError) &&
    !isLoadingWithDelay &&
    items.length === 0

  const handleRetryFriends = useCallback(() => {
    void refreshFriends()
  }, [refreshFriends])

  // Suggested segment renders its own component (own data/states) — not the friends grid.
  const isSuggestedView =
    viewType === VIEW_TYPE.FRIENDS && filterType === FILTER_TYPE.SUGGESTED

  return (
    <View style={friendsPageStyles.container}>
      <DecorativeElements />
      <PageHeader flavor={HEADER_FLAVORS.FRIENDS} />
      <View
        style={[
          friendsPageStyles.belowHeaderContent,
          belowHeaderPaddingTop > 0 && { paddingTop: belowHeaderPaddingTop },
        ]}
      >
        <GuestBanner />
        <View style={friendsPageStyles.toggleContainer} testID="friends-groups-toggle-container">
          <FriendsGroupsToggle
            viewType={viewType}
            onViewTypeChange={handleFriendsGroupsViewChange}
            friendReceivedCount={friendReceivedCount}
            groupReceivedCount={groupReceivedCount}
            groupsTabDisabled={isGuest}
          />
        </View>
        <View style={friendsPageStyles.filterRow}>
          <FriendsFilterButtons
            filterType={filterType}
            counts={{ ...counts, suggested: suggestionsData?.length ?? 0 }}
            onFilterChange={setFilterType}
            viewType={viewType}
            showSuggested={!isGuest}
          />
          {viewType === VIEW_TYPE.FRIENDS ? (
            <InviteFriendsButton label="Invite" onPress={handleInviteFlow} />
          ) : null}
          {!isGuest && viewType === VIEW_TYPE.GROUPS ? (
            <InviteFriendsButton
              label="Create"
              onPress={() => {
                setShowCreateGroupModal(true)
              }}
            />
          ) : null}
        </View>
        <ScrollView
          style={friendsPageStyles.content}
          showsVerticalScrollIndicator={false}
        >
          {isLoadingWithDelay && !isSuggestedView ? (
            <FriendsGridSkeleton
              numColumns={calculateColumns(screenWidth)}
              screenWidth={screenWidth}
              contentWidth={contentWidth}
            />
          ) : showFriendsLoadError && !isSuggestedView ? (
            <DataLoadErrorPanel
              title={FRIENDS_ERROR_COPY.TITLE}
              message={FRIENDS_ERROR_COPY.MESSAGE}
              onRetry={handleRetryFriends}
              testID="friends-load-error"
            />
          ) : (
            <FadeInContent opacity={contentOpacity}>
              <InviteFriendsModal />
              <CreateGroupModal
                isVisible={showCreateGroupModal}
                onDismiss={() => setShowCreateGroupModal(false)}
                onGroupCreated={async () => {
                  await refreshGroups()
                }}
                onShowMessage={(message, variant) =>
                  setSnackbar({
                    message,
                    variant,
                  })
                }
              />
              {isSuggestedView ? (
                <PeopleYouMayKnow
                  userId={suggestionsUserId}
                  enabled={suggestionsEnabled}
                  onInvite={handleInviteFlow}
                  onShowMessage={(message, variant) => setSnackbar({ message, variant })}
                />
              ) : (
                <FriendsList
                  items={items}
                  screenWidth={screenWidth}
                  contentWidth={contentWidth}
                  filterType={filterType}
                  viewType={viewType}
                  onRequestCreateGroup={() => setShowCreateGroupModal(true)}
                  onInviteFriendsPress={handleInviteFlow}
                />
              )}
            </FadeInContent>
          )}
        </ScrollView>
      </View>
    </View>
  )
}

export default function Friends() {
  return (
    <FriendsPageProvider>
      <ErrorBoundary>
        <FriendsContent />
      </ErrorBoundary>
    </FriendsPageProvider>
  )
}
